import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { AuthImage } from '../components/AuthImage';
import axios from 'axios';
import {
  User, Lock, ArrowLeft, Save, Eye, EyeOff, Shield,
  Camera, Phone, FileText, Building2, Upload
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_COLORS = {
  ADMIN: 'bg-red-500/20 text-red-400 border-red-500/30',
  PRODUCER: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  DIRECTOR: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  DEPARTMENT_HEAD: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  CREW: 'bg-green-500/20 text-green-400 border-green-500/30',
  VIEWER: 'bg-[#555]/20 text-[#A0A4B8] border-[#555]/30',
};

const DEPARTMENTS = [
  'ALL','DIRECTION','ART','COSTUME_WARDROBE','CASTING','PRODUCTION',
  'MAKEUP_HAIR','VFX','LOCATIONS','SFX_STUNTS','SOUND','CAMERA','EDITING'
];

function AvatarSection({ user, token, onAvatarUpdate }) {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();
  const headers = { Authorization: `Bearer ${token}` };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/auth/me/avatar`, fd, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }
      });
      onAvatarUpdate(res.data);
      toast.success('Avatar updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  return (
    <div className="flex flex-col items-center gap-3">
      <div className="relative group">
        <div
          className="h-24 w-24 rounded-full border-2 border-[#F5A623]/30 overflow-hidden cursor-pointer hover:opacity-80 transition-opacity bg-[#F5A623]/10 flex items-center justify-center"
          onClick={() => fileRef.current?.click()}
          data-testid="avatar-upload-trigger"
        >
          {user?.avatar_url ? (
            <AuthImage path={user.avatar_url} alt="Avatar" className="h-full w-full object-cover" />
          ) : (
            <span className="font-semibold text-3xl text-[#F5A623]">
              {user?.full_name?.[0]?.toUpperCase() || '?'}
            </span>
          )}
        </div>
        <div
          className="absolute inset-0 rounded-full bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
          onClick={() => fileRef.current?.click()}
        >
          {uploading ? (
            <Upload className="h-5 w-5 text-white animate-pulse" />
          ) : (
            <Camera className="h-5 w-5 text-white" />
          )}
        </div>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFileChange} />
      </div>
      <p className="text-xs text-[#555]">Click to update photo</p>
    </div>
  );
}

export default function ProfilePage() {
  const { user, token, setUser } = useAuth();
  const navigate = useNavigate();

  const [nameValue, setNameValue] = useState(user?.full_name || '');
  const [emailValue, setEmailValue] = useState(user?.email || '');
  const [phoneValue, setPhoneValue] = useState(user?.phone || '');
  const [bioValue, setBioValue] = useState(user?.bio || '');
  const [deptValue, setDeptValue] = useState(user?.department || '');
  const [savingProfile, setSavingProfile] = useState(false);

  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [savingPw, setSavingPw] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      const updates = {};
      if (nameValue.trim() && nameValue.trim() !== user?.full_name) updates.full_name = nameValue.trim();
      if (emailValue && emailValue !== user?.email) updates.email = emailValue;
      if (phoneValue !== (user?.phone || '')) updates.phone = phoneValue;
      if (bioValue !== (user?.bio || '')) updates.bio = bioValue;
      if (deptValue !== user?.department) updates.department = deptValue;
      if (Object.keys(updates).length === 0) { toast.info('No changes'); return; }
      const res = await axios.patch(`${API}/auth/me`, updates, { headers });
      if (setUser) setUser(res.data);
      toast.success('Profile updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPw.length < 6) { toast.error('New password must be at least 6 characters'); return; }
    if (newPw !== confirmPw) { toast.error('Passwords do not match'); return; }
    setSavingPw(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: currentPw, new_password: newPw,
      }, { headers });
      toast.success('Password changed successfully');
      setCurrentPw(''); setNewPw(''); setConfirmPw('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setSavingPw(false);
    }
  };

  const handleAvatarUpdate = (updatedUser) => {
    if (setUser) setUser(updatedUser);
  };

  return (
    <div className="min-h-screen bg-[#0D0F14] text-[#FFFFFF] p-4 sm:p-8">
      <div className="max-w-2xl mx-auto">
        <button
          onClick={() => navigate('/dashboard')}
          className="flex items-center gap-2 text-[#A0A4B8] hover:text-[#FFFFFF] transition-colors mb-8 text-sm"
          data-testid="back-to-dashboard-btn"
        >
          <ArrowLeft className="h-4 w-4" /> Back to Dashboard
        </button>

        <h1 className="font-semibold text-4xl text-[#FFFFFF] mb-1">My Profile</h1>
        <p className="text-[#A0A4B8] text-sm mb-8">Manage your account details, photo, and password.</p>

        {/* Avatar + Overview */}
        <div className="bg-[#13151C] border border-[#1E2028] rounded-xl p-6 mb-6" data-testid="profile-overview">
          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6">
            <AvatarSection user={user} token={token} onAvatarUpdate={handleAvatarUpdate} />
            <div className="flex-1 text-center sm:text-left">
              <div className="font-semibold text-xl text-[#FFFFFF]">{user?.full_name}</div>
              <div className="text-sm text-[#A0A4B8] mt-0.5">{user?.email}</div>
              {user?.bio && <div className="text-sm text-[#888] mt-2 italic">{user?.bio}</div>}
              <div className="flex flex-wrap gap-2 mt-3 justify-center sm:justify-start">
                <Badge className={`${ROLE_COLORS[user?.role] || 'bg-[#1E2028] text-[#A0A4B8]'} border text-xs`}>
                  <Shield className="h-3 w-3 mr-1" />
                  {user?.role?.replace(/_/g, ' ')}
                </Badge>
                <Badge className="bg-[#4a9eff]/20 text-[#4a9eff] border border-[#4a9eff]/30 text-xs">
                  {user?.department?.replace(/_/g, ' ')}
                </Badge>
              </div>
            </div>
          </div>
        </div>

        {/* Edit Profile */}
        <div className="bg-[#13151C] border border-[#1E2028] rounded-xl p-6 mb-6" data-testid="edit-profile-section">
          <div className="flex items-center gap-2 mb-5">
            <User className="h-4 w-4 text-[#F5A623]" />
            <h2 className="text-base font-semibold text-[#FFFFFF]">Profile Details</h2>
          </div>
          <form onSubmit={handleSaveProfile} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-[#A0A4B8] mb-1.5 block">Display Name</label>
                <Input
                  value={nameValue}
                  onChange={e => setNameValue(e.target.value)}
                  className="bg-[#0D0F14] border-[#1E2028] text-[#FFFFFF]"
                  placeholder="Your display name"
                  data-testid="profile-name-input"
                />
              </div>
              <div>
                <label className="text-xs text-[#A0A4B8] mb-1.5 block">Email</label>
                <Input
                  type="email"
                  value={emailValue}
                  onChange={e => setEmailValue(e.target.value)}
                  className="bg-[#0D0F14] border-[#1E2028] text-[#FFFFFF]"
                  placeholder="your@email.com"
                  data-testid="profile-email-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-[#A0A4B8] mb-1.5 block flex items-center gap-1">
                  <Phone className="h-3 w-3" /> Phone
                </label>
                <Input
                  value={phoneValue}
                  onChange={e => setPhoneValue(e.target.value)}
                  className="bg-[#0D0F14] border-[#1E2028] text-[#FFFFFF]"
                  placeholder="+1 234 567 8900"
                  data-testid="profile-phone-input"
                />
              </div>
              <div>
                <label className="text-xs text-[#A0A4B8] mb-1.5 block flex items-center gap-1">
                  <Building2 className="h-3 w-3" /> Department
                </label>
                <select
                  value={deptValue}
                  onChange={e => setDeptValue(e.target.value)}
                  className="w-full bg-[#0D0F14] border border-[#1E2028] text-[#FFFFFF] rounded-md px-3 h-10 text-sm focus:outline-none focus:border-[#F5A623]/50"
                  data-testid="profile-dept-select"
                >
                  {DEPARTMENTS.map(d => (
                    <option key={d} value={d}>{d.replace(/_/g, ' ')}</option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label className="text-xs text-[#A0A4B8] mb-1.5 block flex items-center gap-1">
                <FileText className="h-3 w-3" /> Bio
              </label>
              <Textarea
                value={bioValue}
                onChange={e => setBioValue(e.target.value)}
                className="bg-[#0D0F14] border-[#1E2028] text-[#FFFFFF] resize-none"
                placeholder="A brief bio about yourself..."
                rows={3}
                data-testid="profile-bio-input"
              />
            </div>
            <div className="pt-1">
              <Button
                type="submit"
                disabled={savingProfile}
                className="bg-[#F5A623] hover:bg-[#E0961B] text-black w-full sm:w-auto"
                data-testid="save-profile-btn"
              >
                <Save className="h-4 w-4 mr-1.5" />
                {savingProfile ? 'Saving…' : 'Save Profile'}
              </Button>
            </div>
          </form>
        </div>

        {/* Change Password */}
        <div className="bg-[#13151C] border border-[#1E2028] rounded-xl p-6" data-testid="change-password-section">
          <div className="flex items-center gap-2 mb-5">
            <Lock className="h-4 w-4 text-[#F5A623]" />
            <h2 className="text-base font-semibold text-[#FFFFFF]">Change Password</h2>
          </div>
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div>
              <label className="text-xs text-[#A0A4B8] mb-1.5 block">Current Password</label>
              <div className="relative">
                <Input
                  type={showCurrent ? 'text' : 'password'}
                  value={currentPw}
                  onChange={e => setCurrentPw(e.target.value)}
                  className="bg-[#0D0F14] border-[#1E2028] text-[#FFFFFF] pr-10"
                  placeholder="Enter current password"
                  required
                  data-testid="current-password-input"
                />
                <button type="button" onClick={() => setShowCurrent(s => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A0A4B8] hover:text-[#FFFFFF]">
                  {showCurrent ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-[#A0A4B8] mb-1.5 block">New Password</label>
                <div className="relative">
                  <Input
                    type={showNew ? 'text' : 'password'}
                    value={newPw}
                    onChange={e => setNewPw(e.target.value)}
                    className="bg-[#0D0F14] border-[#1E2028] text-[#FFFFFF] pr-10"
                    placeholder="At least 6 characters"
                    required
                    data-testid="new-password-input"
                  />
                  <button type="button" onClick={() => setShowNew(s => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A0A4B8] hover:text-[#FFFFFF]">
                    {showNew ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="text-xs text-[#A0A4B8] mb-1.5 block">Confirm Password</label>
                <Input
                  type="password"
                  value={confirmPw}
                  onChange={e => setConfirmPw(e.target.value)}
                  className={`bg-[#0D0F14] border-[#1E2028] text-[#FFFFFF] ${confirmPw && confirmPw !== newPw ? 'border-red-500/60' : ''}`}
                  placeholder="Repeat new password"
                  required
                  data-testid="confirm-password-input"
                />
                {confirmPw && confirmPw !== newPw && (
                  <p className="text-xs text-red-400 mt-1">Passwords do not match</p>
                )}
              </div>
            </div>
            <div className="pt-1">
              <Button
                type="submit"
                disabled={savingPw || !currentPw || !newPw || !confirmPw}
                className="bg-[#F5A623] hover:bg-[#E0961B] text-black w-full sm:w-auto"
                data-testid="change-password-btn"
              >
                {savingPw ? 'Changing Password…' : 'Change Password'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
